"use client";

import clsx from "clsx";
import React from "react";

/**
 * Shared Excel-style table renderer. Two density variants:
 * - "full": used inside the artifact panel with sticky header, wider
 *   padding, break-word cells, and hover highlight.
 * - "peek": used in the artifact card preview — compact, truncated,
 *   no hover/sticky affordances.
 */
const ExcelTable: React.FC<{rows: string[][]; density?: "full" | "peek"}> = ({
   rows,
   density = "full",
}) => {
   if (!rows || rows.length === 0) return null;
   const isFull = density === "full";
   const [header, ...body] = rows;

   return (
      <table
         className={clsx(
            "border-collapse",
            isFull ? "text-xs" : "w-full text-[11px]"
         )}
         style={isFull ? {tableLayout: "auto", minWidth: "100%"} : undefined}
      >
         <thead className={isFull ? "sticky top-0 z-10" : undefined}>
            <tr>
               {(header ?? []).map((cell, c) => (
                  <th
                     key={`h-${c}-${cell ?? ""}`}
                     className={clsx(
                        "text-left font-medium whitespace-nowrap",
                        "text-800 border-b-2 border-primary",
                        isFull ? "px-3 py-2 bg-panel min-w-[160px]" : "px-2 py-1.5"
                     )}
                  >
                     {cell ?? ""}
                  </th>
               ))}
            </tr>
         </thead>
         <tbody>
            {body.map((row, r) => (
               <tr
                  key={`r-${r}-${(row ?? [])[0] ?? ""}`}
                  className={clsx(
                     r % 2 === 1 && "bg-100/60",
                     isFull && "transition-colors hover:bg-primary/5"
                  )}
               >
                  {(row || []).map((cell, c) => (
                     <td
                        key={`c-${r}-${c}-${cell ?? ""}`}
                        className={clsx(
                           "text-800 align-top",
                           "border-b border-200 last:border-b-0",
                           isFull
                              ? "px-3 py-2 min-w-[160px] max-w-[360px] whitespace-normal break-words leading-5"
                              : "px-2 py-1 truncate max-w-[180px]"
                        )}
                     >
                        {cell ?? ""}
                     </td>
                  ))}
               </tr>
            ))}
         </tbody>
      </table>
   );
};

export default ExcelTable;
